import { CheckOutlined, CloseOutlined, FrownFilled, SmileOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Empty, Modal, Tag } from 'antd';
import { AxiosError } from 'axios';
import SymptomTag from 'components/SymptomTag';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import ApiSymptomTrackingManager from 'services/api/ApiSymptomTrackingManager';
import { colors } from 'design';

const useStyles = createUseStyles({
  pageContainer: {
    height: '100%',
    marginTop: '-15px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '& h2': {
      marginBottom: 0,
    },
  },
  container: {
    height: '100%',
    paddingLeft: 8,
    paddingRight: 8,
    backgroundColor: colors.surface.page,
    overflowY: 'scroll',
  },
  profileCard: {
    margin: '10px auto',
    '& h4': {
      marginTop: 24,
      marginBottom: 4,
      color: colors.text.tertiary,
    },
  },
  noAnswer: {
    fontStyle: 'italic',
  },
  personalInfo: {
    marginTop: 0,
    color: colors.text.tertiary,
  },
  painTag: {
    padding: '8px 16px',
    fontSize: 15,
  },
  yesNoAnswer: {
    '& .anticon': {
      marginRight: 8,
    },

    '& .anticon-close': {
      color: colors.dataViz.veryBad,
    },
    '& .anticon-check': {
      color: colors.dataViz.veryGood,
    },
  },
  extraField: {
    fontStyle: 'italic',
    backgroundColor: colors.surface.page,
    borderRadius: 8,
    padding: 8,
  },
});

export type PatientProfileModalProps = {
  patientId: string;
  patientName: string;
  open?: boolean;
  onClose?: () => void;
};

const PatientProfileModal: React.FC<PatientProfileModalProps> = ({
  patientId,
  patientName,
  open = false,
  onClose = undefined,
}) => {
  // Hooks
  const styles = useStyles();
  const { t } = useTranslation();

  // State
  const [isModalOpen, setIsModalOpen] = useState(open);

  const { data: survey, loading: loadingOnboardingSurvey } = useRequest(
    () => ApiSymptomTrackingManager.getOnboardingSurvey(patientId),
    {
      onError: error => {
        // ignore 404 errors as not all users have filled out the onboarding survey
        if (!(error instanceof AxiosError && error.response?.status === 404)) {
          console.error(error);
        }
      },
    },
  );

  const { data: surveyV2, loading: loadingOnboardingSurveyV2 } = useRequest(
    () => ApiSymptomTrackingManager.getOnboardingSurveyV2(patientId),
    {
      onError: error => {
        // ignore 404 errors as not all users have filled out the onboarding survey v2
        if (!(error instanceof AxiosError && error.response?.status === 404)) {
          console.error(error);
        }
      },
    },
  );

  const { data: file, loading: loadingPatientFile } = useRequest(
    () => ApiSymptomTrackingManager.getPatientFile(patientId),
    {
      onError: error => {
        // ignore 404 errors as not all users have filled out the patient file
        if (!(error instanceof AxiosError && error.response?.status === 404)) {
          console.error(error);
        }
      },
    },
  );

  const handleOk = () => {
    setIsModalOpen(false);
    if (onClose) {
      onClose();
    }
  };

  return (
    <Modal
      open={isModalOpen}
      onOk={handleOk}
      loading={loadingOnboardingSurvey || loadingOnboardingSurveyV2 || loadingPatientFile}
      cancelButtonProps={{ style: { display: 'none' } }}
      okText={t('global.buttons.close')}
      closable={false}
    >
      <div className={styles.pageContainer}>
        {!survey && !surveyV2 && !file && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('patientProfile.noData')} />
        )}
        {(survey || surveyV2) && file && (
          <div className={styles.profileCard}>
            <h2>{patientName}</h2>
            <p className={styles.personalInfo}>
              {t('patientProfile.bornIn', { year: file.birth_year, age: new Date().getFullYear() - file.birth_year })}
            </p>

            {/* Display new questionnaire if available (prioritize V2) */}
            {surveyV2 ? (
              <>
                <h4>{t('patientProfile.pathologies')}</h4>
                {surveyV2.pathologies.map(pathology => (
                  <Tag key={pathology}>{t(`onboarding.questionnaire.pathologies.options.${pathology}`)}</Tag>
                ))}
                {surveyV2.extra_pathologies && <p className={styles.extraField}>{surveyV2.extra_pathologies}</p>}
                {surveyV2.pathologies.length === 0 && !surveyV2.extra_pathologies && (
                  <p className={styles.noAnswer}>{t('patientProfile.noPathologies')}</p>
                )}

                <h4>{t('patientProfile.chronicSymptoms')}</h4>
                {surveyV2.chronic_symptom_duration ? (
                  t(`onboarding.questionnaire.chronicSymptoms.options.${surveyV2.chronic_symptom_duration}`)
                ) : (
                  <p className={styles.noAnswer}>{t('patientProfile.noAnswer')}</p>
                )}

                {surveyV2.chronic_symptom_duration && surveyV2.chronic_symptom_duration !== 'NO' && (
                  <>
                    <h4>{t('patientProfile.symptomImpact')}</h4>
                    {surveyV2.symptom_impact ? (
                      t(`onboarding.questionnaire.chronicSymptoms.impact.options.${surveyV2.symptom_impact}`)
                    ) : (
                      <p className={styles.noAnswer}>{t('patientProfile.noAnswer')}</p>
                    )}

                    <h4>{t('patientProfile.consultationCount')}</h4>
                    {surveyV2.consultation_count ? (
                      t(`onboarding.questionnaire.chronicSymptoms.consultation.options.${surveyV2.consultation_count}`)
                    ) : (
                      <p className={styles.noAnswer}>{t('patientProfile.noAnswer')}</p>
                    )}
                  </>
                )}
              </>
            ) : (
              /* Display old questionnaire if V2 is not available */
              survey && (
                <>
                  <h4>{t('patientProfile.feels_pain_or_discomfort')}</h4>
                  {survey.feels_pain_or_discomfort ? (
                    <Tag className={styles.painTag} color={colors.dataViz.veryBad} icon={<FrownFilled />}>
                      {t('patientProfile.yes')}
                    </Tag>
                  ) : (
                    <Tag className={styles.painTag} color={colors.dataViz.veryGood} icon={<SmileOutlined />}>
                      {t('patientProfile.no')}
                    </Tag>
                  )}

                  <h4>{t('patientProfile.symptoms')}</h4>
                  {survey.symptoms.map(symptom => (
                    <SymptomTag key={symptom} symptom={symptom} />
                  ))}
                  {survey.extra_symptoms && <p className={styles.extraField}>{survey.extra_symptoms}</p>}
                  {survey.symptoms.length === 0 && !survey.extra_symptoms && (
                    <p className={styles.noAnswer}>{t('patientProfile.noSymptoms')}</p>
                  )}

                  <h4>{t('patientProfile.pathologies')}</h4>
                  {survey.pathologies.map(pathology => (
                    <Tag key={pathology}>{t(`onboarding.questionnaire.pathologies.options.${pathology}`)}</Tag>
                  ))}
                  {survey.extra_pathologies && <p className={styles.extraField}>{survey.extra_pathologies}</p>}
                  {survey.pathologies.length === 0 && !survey.extra_pathologies && (
                    <p className={styles.noAnswer}>{t('patientProfile.noPathologies')}</p>
                  )}

                  <h4>{t('patientProfile.expectations')}</h4>
                  {survey.expectations.map(expectation => (
                    <Tag key={expectation}>{t(`onboarding.questionnaire.expectations.options.${expectation}`)}</Tag>
                  ))}
                  {survey.extra_expectations && <p className={styles.extraField}>{survey.extra_expectations}</p>}
                  {survey.expectations.length === 0 && !survey.extra_expectations && (
                    <p className={styles.noAnswer}>{t('patientProfile.noExpectations')}</p>
                  )}

                  <h4>{t('patientProfile.questions')}</h4>
                  {survey.questions.map(question => (
                    <Tag key={question}>{t(`onboarding.questionnaire.questions.options.${question}`)}</Tag>
                  ))}
                  {survey.extra_questions && <p className={styles.extraField}>{survey.extra_questions}</p>}
                  {survey.questions.length === 0 && !survey.extra_questions && (
                    <p className={styles.noAnswer}>{t('patientProfile.noQuestions')}</p>
                  )}
                </>
              )
            )}

            <h4>{t('patientProfile.file')}</h4>
            <p className={styles.yesNoAnswer}>
              {file.takes_contraception ? (
                <>
                  <CheckOutlined />
                  {t('patientProfile.takesContraception')}
                </>
              ) : (
                <>
                  <CloseOutlined />
                  {t('patientProfile.takesNoContraception')}
                </>
              )}
            </p>
            <p className={styles.yesNoAnswer}>
              {file.has_children ? (
                <>
                  <CheckOutlined />
                  {t('patientProfile.hasChildren')}
                </>
              ) : (
                <>
                  <CloseOutlined />
                  {t('patientProfile.hasNoChildren')}
                </>
              )}
            </p>
            <p className={styles.yesNoAnswer}>
              {file.is_pregnant ? (
                <>
                  <CheckOutlined />
                  {t('patientProfile.isPregnant')}
                </>
              ) : (
                <>
                  <CloseOutlined />
                  {t('patientProfile.isNotPregnant')}
                </>
              )}
            </p>
            <p className={styles.yesNoAnswer}>
              {file.is_trying_to_conceive ? (
                <>
                  <CheckOutlined />
                  {t('patientProfile.isTryingToConceive')}
                </>
              ) : (
                <>
                  <CloseOutlined />
                  {t('patientProfile.isNotTryingToConceive')}
                </>
              )}
            </p>
            <p className={styles.yesNoAnswer}>
              {file.is_taking_treatment ? (
                <>
                  <CheckOutlined />
                  {t('patientProfile.isTakingTreatment')}
                </>
              ) : (
                <>
                  <CloseOutlined />
                  {t('patientProfile.isNotTakingTreatment')}
                </>
              )}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};
export default PatientProfileModal;
